import { Container } from '../../../Components/Container';
import React , { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import PercentIcon from '@mui/icons-material/Percent';
import { Column, Row } from '../../../Components/Grid';
import { useQuery } from 'react-query';
import { getGapAnalysisDashboard } from '../../../services/gap-analysis';
import { useParams } from 'react-router-dom';
import { PageLoaderShow } from '../../../Components/PageLoader/Show';
import { typeLabels, statusControl, maturityLevels } from '../../../utils/labels_utils'
import { tableHeader } from './table-header'
import { tableControls } from './table-controls'
import { SummaryCard } from '../../../Components/SummaryCard'
import EntityProgressTable from '../../../Components/EntityProgressTable'
import ExpandableTable from '../../../Components/ExpandableTable';
import StatusStackedBarChart from '../../../Components/StatusStackedBarChart';
import GroupedProgressCard from '../../../Components/GroupedProgressCard';
import DonutChartCard from '../../../Components/DonutChartCard';
import PieChartWithLegend from '../../../Components/PieChartWithLegend';
import ProgressInfoCard from '../../../Components/ProgressInfoCard';
import ChartLine from '../../../Components/LineChart';
import CardBarHorizontal from '../../../Components/CardBarHorizontal';

import { Tabs, Tab, Box } from '@mui/material';
import { Card } from '../../../Components/Card';


export const GapAnalysisDashboard = () => {
  const { t } = useTranslation(['gap-analysis', 'common']);
  const { id } = useParams();
  const [tabIndex, setTabIndex] = useState(0);

  const {
    isLoading,
    error,
    data: data_dashboard
  } = useQuery('getGapAnalysisDashboard', async () => {
    const data = await getGapAnalysisDashboard(id);
    return data.status === 200 ? data.data : null;
  });
  if (isLoading) return <p>Carregando...</p>;
  if (error) return <p>Erro ao carregar os dados.</p>;
  const entitiesByType = data_dashboard.entitiesByType || {};
  const types = Object.keys(entitiesByType);
  const handleChange = (_event, newIndex) => {
    setTabIndex(newIndex);
  };
  const totalEntities = Object.values(data_dashboard.entityCount).reduce((acc, curr) => acc + curr, 0);
  const entidades = Object.keys(data_dashboard.entityCount).map((key) => ({
    name: typeLabels[key],
    value: data_dashboard.entityCount[key],
    percent: ((data_dashboard.entityCount[key] / totalEntities) * 100).toFixed(2)
  }));

  const total = Object.values(data_dashboard.maturityCounts).reduce((acc, curr) => acc + curr, 0);
  const maturityData = Object.keys(maturityLevels).map((key) => ({
    name: maturityLevels[key],
    value: data_dashboard.maturityCounts[key],
    total: total
  }));

  const entityCountsByTypeWithStatus = data_dashboard.entityCountsByTypeWithStatus;
  const dataEntityCountsByTypeWithStatus = Object.keys(entityCountsByTypeWithStatus).map((key) => ({
    entidade: typeLabels[key],
    ...entityCountsByTypeWithStatus[key]
  }));

  const entityProgress = data_dashboard.entityTypeProcess;
  const dataEntityProgress = Object.keys(entityProgress).map((key) => ({
    ...entityProgress[key],
    label: typeLabels[entityProgress[key].label],
  }));

  const progress = data_dashboard.totalControls > 0 ? (data_dashboard.linkedControls / data_dashboard.totalControls) * 100 : 0;

  const pieChartData = Object.keys(data_dashboard.statusCounts).map((key) => ({
    name: statusControl[key].label,
    value: data_dashboard.statusCounts[key],
    color: statusControl[key].color
  }));

  const link_ = `gap-analysis/${id}`;
  const dataProgressFramewok = data_dashboard.progressoPorFramework.map(item => ({
    label: item.name,
    value: item.progress
  }));

  return (
    <>
      {!isLoading && !error && (
        <Container title={`${data_dashboard.gapAnalysis?.name}`}
                   breadcrumb={`Assessment / Gap Analysis / ${data_dashboard.gapAnalysis?.name}`}
                   linkPage="/gap-analysis"
        >
          <Row md={'3'} className="mt-1 g-1">
            <Column xs={12} md={4} className="p-1" >
              <Grid item xs={12}>
                <ProgressInfoCard
                  title={t('gap-analysis:dashboard.progressTitle')}
                  description={t('gap-analysis:dashboard.progressDesc')}
                  icon={<PercentIcon sx={{ color: "#0039c4", fontSize: 20}} />}
                  total={100}
                  value={data_dashboard.overallProgress}
                  progress={data_dashboard.overallProgress}
                  unitLabel={t('gap-analysis:dashboard.unitCompleted')}
                />
              </Grid>
              <Grid item xs={12} className="mt-1" >
                <ProgressInfoCard
                  title={t('gap-analysis:dashboard.linkedControlsTitle')}
                  description={t('gap-analysis:dashboard.linkedControlsDesc')}
                  icon={<SecurityIcon sx={{ color: "#0039c4", fontSize: 20 }} />}
                  total={data_dashboard.totalControls}
                  value={data_dashboard.linkedControls}
                  progress={progress}
                  unitLabel={t('gap-analysis:dashboard.unitLinked')}
                />
              </Grid>
            </Column>
            <Column xs={12} md={8} className="p-1">
              <ChartLine title={t('gap-analysis:dashboard.history')} data={data_dashboard.historicalProgress} tooltipLabel={t('gap-analysis:dashboard.tooltipProgress')} color="#0039c4" />
            </Column>
          </Row>
          <Row className="g-1">
            <Column xs={12} md={4} className="p-1">
              <CardBarHorizontal
                title={t('gap-analysis:dashboard.progressByFramework')}
                data={dataProgressFramewok}
                valueLabel={t('gap-analysis:dashboard.tooltipProgress')}
              />
            </Column>
            <Column xs={12} md={5} className="p-1">
              <PieChartWithLegend title={t('gap-analysis:dashboard.statusDistribution')} data={pieChartData} />
            </Column>
            <Column xs={12} md={3} className="p-1" >
              <GroupedProgressCard title={t('gap-analysis:dashboard.maturityLevelTitle')} data={maturityData} />
            </Column>
          </Row>
          <Row className="g-1">
            <Column xs={12} md={3} className="p-1">
              <DonutChartCard
                title={t('gap-analysis:dashboard.controlsByEntity')}
                total={data_dashboard.linkedControls}
                data={entidades}
              />
            </Column>
            <Column xs={12} md={5} className="p-1">
              <StatusStackedBarChart
                title={t('gap-analysis:dashboard.statusByEntity')}
                data={dataEntityCountsByTypeWithStatus}
                categoryKey="entidade"
              />
            </Column>
            <Column xs={12} md={4} className="p-1">
              <Grid item >
                <SummaryCard title={t('gap-analysis:dashboard.uniqueEntities')} value={data_dashboard.uniqueEntityCount} />
              </Grid>
              <Grid item className="mt-1" >
                <EntityProgressTable
                  title={t('gap-analysis:dashboard.entityProgress')}
                  data={dataEntityProgress}
                />
              </Grid>
            </Column>
          </Row>
          <Row className="g-1">
            <Card>
              <Tabs value={tabIndex} onChange={handleChange}>
                {types.map((type) => (
                  <Tab key={type} label={typeLabels[type]} />
                ))}
              </Tabs>

              <Box mt={2}>
                {types.map((type, index) => (
                  <div key={type} hidden={tabIndex !== index}>
                    {tabIndex === index && (
                      <ExpandableTable
                        rows={entitiesByType[type]}
                        columns={tableHeader(t)}
                        link={link_}
                        linkExtend="gap-analysis/control"
                        entityColumns={tableControls(t)}
                        enableSearch={true}
                        enableSorting={true}
                        title={t('gap-analysis:dashboard.controlsByType', { type: typeLabels[type] })}
                      />
                    )}
                  </div>
                ))}
              </Box>
            </Card>
          </Row>
        </Container>
      )}
      {isLoading && <PageLoaderShow />}
      {error && <div>Erro</div>}
    </>
  )

}